import { ApiTab } from '@/Enums/Attribution/ApiTabs.enum';
import TabPanel from '@/components/TabPanel';
import { API_TABS_BASE_TAB_ID } from '@/constants/attribution';
import { HEADER_HEIGHT } from '@/constants/layout';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { selectVisibleInsightsTabs } from '@/store/selectors/currentWhitelabel.selectors';
import { selectHideWhitelabels } from '@/store/selectors/me.selectors';
import { getClients, getWhitelabels } from '@/store/slices/sharedApi.slice';
import { Box, Typography } from '@mui/material';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import ApiTabs from './Components/ApiTabs';
import FootTrafficTab from './FootTrafficTab';
import OfflineSalesTab from './OfflineSalesTab';
import WebVisitsTab from './WebVisitsTab';

const Attribution = () => {
  const printContainerRef = useRef<HTMLDivElement>(null);
  const dispatch = useAppDispatch();

  const [searchParams, setSearchParams] = useSearchParams({ tab: ApiTab.WebVisits });
  const currentTab = searchParams.get('tab') as ApiTab;

  const hideWhitelabels = useAppSelector(selectHideWhitelabels);
  const visibleTabs = useAppSelector(selectVisibleInsightsTabs);

  const commonTabPanelProps = useMemo(
    () => ({
      baseTabId: API_TABS_BASE_TAB_ID,
      value: currentTab,
    }),
    [currentTab],
  );

  const setCurrentTab = useCallback(
    (tab: ApiTab) => {
      setSearchParams({ tab });
    },
    [setSearchParams],
  );

  useEffect(() => {
    if (visibleTabs.length && !visibleTabs.includes(currentTab)) {
      setSearchParams({ tab: visibleTabs[0] }, { replace: true });
    }
  }, [currentTab, visibleTabs, setSearchParams]);

  useEffect(() => {
    if (!hideWhitelabels) {
      dispatch(getWhitelabels());
    } else {
      dispatch(getClients());
    }
  }, [dispatch, hideWhitelabels]);

  return (
    <Box
      sx={{
        height: `calc(100vh - ${HEADER_HEIGHT}px)`,
        '@media print': {
          height: 'auto',
          padding: '10px',
        },
      }}
      className='w-full space-y-4 overflow-y-auto print:space-y-[10px]'
    >
      <div className='w-full space-y-[30px] p-[30px]' ref={printContainerRef}>
        {visibleTabs.length === 0 ? (
          <Typography color='text.secondary'>No insights are enabled for your organization.</Typography>
        ) : (
          <>
            <ApiTabs currentTab={currentTab} onTabChange={setCurrentTab} printContainerRef={printContainerRef} />
            {visibleTabs.includes(ApiTab.WebVisits) && (
              <TabPanel {...commonTabPanelProps} index={ApiTab.WebVisits}>
                <WebVisitsTab />
              </TabPanel>
            )}
            {visibleTabs.includes(ApiTab.OfflineSales) && (
              <TabPanel {...commonTabPanelProps} index={ApiTab.OfflineSales}>
                <OfflineSalesTab />
              </TabPanel>
            )}
            {visibleTabs.includes(ApiTab.FootTraffic) && (
              <TabPanel {...commonTabPanelProps} index={ApiTab.FootTraffic}>
                <FootTrafficTab />
              </TabPanel>
            )}
          </>
        )}
      </div>
    </Box>
  );
};

export default Attribution;
